import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddNewIcon3,
  CloseIcon,
  DeleteBin6LineIcon,
  InformationFillIcon,
  RotationLock2,
  UploadIcon,
} from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { downloadFile, getFileName, handleApiError, notify } from '~/utils/helper';
import { IAccount, IEditAccount } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import { ROLE_AGENT, ROLE_DISTRIBUTOR, ROLE_USER } from '~/utils/constants/account';
import { IMasterDataItem } from '~/utils/interface/master';
import utilityApi from '~/apis/utility';
import deviceApi from '~/apis/device';
import { IEquipment } from '~/utils/interface/equipment';
import { BaseScrollbar } from '~/components/common';
import facilityApi from '~/apis/facility';
import { IFacility } from '~/utils/interface/facility';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { useFormik } from 'formik';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { useDropzone } from 'react-dropzone';
import { commonActions } from '~/thunks/common/commonSlice';
import fileApi from '~/apis/file';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import ShowUserDialog from '~/pages/admin/users/component/show-user-dialog/ShowUserDialog';
import equipmentApi from '~/apis/equipment';
import DetailEquipmentDialog from '~/pages/admin/equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import DetailFacilityDialog from '~/pages/admin/facilities/components/detail-facility-dialog/DetailFacilityDialog';
import DetailAgentDialog from '~/pages/admin/agents/component/detail-agent-dialog/DetailAgentDialog';
import SendPasswordDialog from '../send-password-dialog/SendPasswordDialog';
import { useAppDispatch } from '~/redux/hooks';

type EditDistributorDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IAccount;
  mode?: string;
};

type StatusColor = { [key: string]: string };
const statusColors: StatusColor = {
  0: '#72777B',
  1: '#3D9149',
  2: '#FF9500',
  3: '#F32A4C',
};

const initialValues: IEditAccount = {
  parent_username: '',
  contract_id: '',
  created_at: '',
  name: '',
  address: '',
  representative: '',
  phone: '',
  email: '',
  username: '',
  files: [],
};

const EditDistributorDialog = (props: EditDistributorDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/distributor', 'admin/common']);

  const [facilityList, setFacilityList] = useState<IFacility[]>([]);
  const [facility, setFacility] = useState<IFacility>();

  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);

  const [distributor, setDistributor] = useState<IAccount>();
  const [userList, setUserList] = useState<IAccount[]>();
  const [agentList, setAgentList] = useState<IAccount[]>([]);

  const [isOpenDetailFacilityDialog, setIsOpenDetailFacilityDialog] = useState<boolean>(false);
  const [isOpenEditAgentDialog, setIsOpenEditAgentDialog] = useState<boolean>(false);
  const [isOpenDetailUserDialog, setIsOpenDetailUserDialog] = useState<boolean>(false);
  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);
  const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState<boolean>(false);
  const [openSendPasswordDialog, setOpenSendPasswordDialog] = useState<boolean>(false);

  const [distributorLevelOptions, setDistributorLevelOptions] = useState<IMasterDataItem[]>([]);

  const isSendEmail = useRef<boolean>(false);
  const dataDetail = useRef<any>();
  const modeAgent = useRef<string>();
  const roleType = useRef<string>();

  const [equipmentFilter, setEquipmentFilter] = useState<number>(-1);
  const [facilityFilter, setFacilityFilter] = useState<number>(-1);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values) => {
      if (props.mode === DIALOG_MODE_CREATE) {
        dispatch(commonActions.setIsLoading(true));
        try {
          const response = await accountApi.createByRole({ role: ROLE_DISTRIBUTOR }, values);
          notify('success', t('saveSuccess', { ns: 'admin/common' }));
          if (isSendEmail.current) {
            setDistributor(response.data);
            setOpenSendPasswordDialog(true);
          } else {
            handleSetOpen(false, true);
          }
        } catch (e: any) {
          handleApiError(e);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }

      if (props.mode === DIALOG_MODE_EDIT) {
        dispatch(commonActions.setIsLoading(true));
        try {
          await accountApi.updateByRole(values);
          notify('success', t('saveSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }
    },
  });

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop: async (acceptedFiles: Array<File>) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', acceptedFiles[0]);

        const response = await fileApi.uploadFiles(uploadForm);
        if (response.data) {
          form.setFieldValue('files', [...form.values.files, response.data.url]);
        }
      } catch (e: any) {
        handleApiError(e);
      }

      dispatch(commonActions.setIsLoading(false));
    },
  });

  useEffect(() => {
    if (props.open && props.data && (props.mode === DIALOG_MODE_EDIT || props.mode === DIALOG_MODE_VIEW)) {
      (async () => {
        try {
          const response = await accountApi.getByRoleList(ROLE_DISTRIBUTOR, { username: props.data?.username });
          if (response.data?.data?.length) {
            const data = response.data.data[0];
            setDistributor(data);

            if (props.mode === DIALOG_MODE_EDIT) {
              form.setValues({
                parent_username: data.parent_username,
                contract_id: data.contract_id,
                created_at: data.created_at,
                name: data.name,
                address: data.address,
                representative: data.representative,
                phone: data.phone,
                email: data.email,
                username: data.username,
                files: data.files,
                distributor_level: String(data.distributor_level),
              });
            }
          }
        } catch (error) {
          handleApiError(error);
        }
      })();
      handleGetListFacility();
      handleGetAgents();
      handleGetUsers();
      handleGetDevices();
      handleGetDistributorLevels();
      setEquipmentFilter(-1);
      setFacilityFilter(-1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.mode]);

  const handleGetDevices = async () => {
    try {
      const res = await deviceApi.getByUserList(String(props?.data?.username));
      setEquipmentList(res.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGetAgents = async () => {
    try {
      const response = await accountApi.getByRole({ role: ROLE_AGENT, username: props?.data?.username });
      setAgentList(response.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGetUsers = async () => {
    try {
      const response = await accountApi.getByRole({ role: ROLE_USER, username: props?.data?.username });
      setUserList(response.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGetListFacility = async () => {
    try {
      const res = await facilityApi.getListFacilityByUser(String(props?.data?.username));
      setFacilityList(res.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGetDistributorLevels = async () => {
    try {
      const response = await utilityApi.getListDistributorLevels();
      let distributorLevelOptions: any = response.data.filter(
        (item) => item.level_id === 1 || item.level_id === Number(props.data?.distributor_level)
      );
      if (Number(props.data?.distributor_level) === 1) {
        distributorLevelOptions.push({ level_id: 2, level_name: t('auto') });
      }

      setDistributorLevelOptions(
        distributorLevelOptions.map((item: any) => {
          return {
            id: String(item.level_id),
            name: item.level_name,
            data0: item.level_name,
          };
        })
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    isSendEmail.current = false;

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleDelete = async () => {
    try {
      if (dataDetail.current && roleType.current) {
        if (roleType.current === 'equipment') {
          await equipmentApi.deleteDevice(dataDetail.current?.device_id);
        } else {
          await accountApi.deleteByRole({
            username: dataDetail.current.username,
            role: roleType.current,
          });
        }

        if (roleType.current === ROLE_USER) {
          handleGetUsers();
        }
        if (roleType.current === 'equipment') {
          handleGetDevices();
        }
        if (roleType.current === ROLE_AGENT) {
          handleGetAgents();
        }

        notify('success', t(`deleteDialog.${roleType.current}.deleteSuccess`));
        setIsOpenDeleteConfirm(false);
      }
    } catch (error) {
      handleApiError(error);
      setIsOpenDeleteConfirm(false);
    }
  };

  const getEquipmentDetail = async (row: IEquipment) => {
    try {
      const res = await equipmentApi.getOverviewData({
        device_code: row.device_code,
      });
      return res.data.devices[0];
    } catch (error) {
      handleApiError(error);
      return {};
    }
  };

  const getLevelColor = (level: number) => {
    if (level === 1) return 'border-[#72777B] text-[#72777B]';
    if (level === 2) return 'border-[#FF9500] text-[#FF9500]';
    if (level === 3) return 'border-[#3D9149] text-[#3D9149]';
    return 'border-[#005F73] text-[#005F73]';
  };
  const getLevel = (level: number) => {
    const name = distributorLevelOptions?.find((item) => Number(item.id) === Number(level))?.name || '';

    return <Box className={`border rounded-[20px] p-1 text-center ${getLevelColor(level)}`}>{name}</Box>;
  };

  const getStatus = (status: number | undefined) => {
    const color = statusColors[String(status) as keyof StatusColor];
    return <Box className={`text-[${color}] text-[${color}] font-bold`}>{t(`simStatuses.${status}`)}</Box>;
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => handleSetOpen(false)}
        fullWidth
        maxWidth={props?.mode === DIALOG_MODE_CREATE ? 'md' : 'xl'}
      >
        <DialogTitle className='bg-[#005F73]'>
          {props.mode === DIALOG_MODE_CREATE ? (
            t('editDialog.createTitle')
          ) : props.mode === DIALOG_MODE_EDIT ? (
            <Box className='flex gap-[25px]'>
              {t('editDialog.editTitle')}
              <RotationLock2 className='cursor-pointer' onClick={() => setOpenSendPasswordDialog(true)} />
            </Box>
          ) : (
            t('editDialog.title')
          )}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BaseRow>
            <Box
              className={`${
                props.mode === DIALOG_MODE_CREATE
                  ? 'w-full'
                  : 'w-1/2 border-r border-solid border-[#DFDFDF] flex flex-col'
              } ${props.mode === DIALOG_MODE_EDIT ? 'pr-[42px]' : ''} relative `}
            >
              {props.mode === DIALOG_MODE_CREATE || props.mode === DIALOG_MODE_EDIT ? (
                <>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-1' : 'order-8'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('editDialog.contract_id')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='contract_id'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.contract_id}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.contract_id)}
                      helperText={form.errors?.contract_id}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-2' : 'order-9'}`}
                  >
                    <InputLabel className='w-1/2'>{t('editDialog.created_at')}:</InputLabel>
                    <Box className='w-1/2'>
                      <BaseDatePicker
                        className='w-full'
                        portalId='datePicker-portalId'
                        value={form.values.created_at}
                        dateFormat='YYYY-MM-DD'
                        onChange={(date) => {
                          form.setFieldValue('created_at', dayjs(date));
                        }}
                        placeholder={t('select', { ns: 'admin/common' })}
                      />
                    </Box>
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-3' : 'order-1'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>
                        {props.mode === DIALOG_MODE_CREATE ? t('editDialog.name2') : t('editDialog.name')}:
                      </InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='name'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.name}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.name)}
                      helperText={form.errors?.name}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-4' : 'order-2'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>
                        {props.mode === DIALOG_MODE_CREATE ? t('editDialog.username2') : t('editDialog.username')}:
                      </InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='username'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.username}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.username)}
                      helperText={form.errors?.username}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-5' : 'order-4'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('editDialog.address')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='address'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.address}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.address)}
                      helperText={form.errors?.address}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-6' : 'order-5'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('editDialog.representative')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='representative'
                      placeholder={t('enter', { ns: 'admin/common' })}
                      value={form.values?.representative}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.representative)}
                      helperText={form.errors?.representative}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-7' : 'order-6'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('editDialog.phone')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='phone'
                      placeholder={t('editDialog.enterPhone')}
                      value={form.values?.phone}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.phone)}
                      helperText={form.errors?.phone}
                    />
                  </BaseRow>
                  <BaseRow
                    className={`gap-5 mb-5 !items-center ${props?.mode === DIALOG_MODE_CREATE ? 'order-8' : 'order-7'}`}
                  >
                    <Box className='w-1/2 font-bold flex items-center'>
                      <InputLabel>{t('editDialog.email')}:</InputLabel>
                    </Box>
                    <TextField
                      size='medium'
                      className='w-1/2'
                      name='email'
                      placeholder={t('editDialog.enterEmail')}
                      value={form.values?.email}
                      onChange={form.handleChange}
                      error={Boolean(form.errors?.email)}
                      helperText={form.errors?.email}
                    />
                  </BaseRow>
                  {props.mode === DIALOG_MODE_EDIT && (
                    <BaseRow className='gap-5 mb-5 !items-center order-3'>
                      <InputLabel className='w-1/2'>{t('editDialog.level')}:</InputLabel>
                      <Box className='w-1/2'>
                        <BaseSelect
                          size='medium'
                          options={distributorLevelOptions}
                          value={form.values.distributor_level}
                          placeholder={t('select')}
                          name='distributor_level'
                          isSearchEnable
                          onChange={form.handleChange}
                          error={Boolean(form.errors?.distributor_level)}
                          helperText={form.errors?.distributor_level}
                          displayInvalidValue
                        />
                      </Box>
                    </BaseRow>
                  )}
                  <BaseRow
                    className={`gap-5 !items-start !justify-between ${
                      props?.mode === DIALOG_MODE_CREATE ? 'order-9' : 'order-10'
                    }`}
                  >
                    <Box className='w-1/2 font-bold'>
                      <InputLabel>{t('editDialog.file')}:</InputLabel>
                      <Box>
                        {form.values.files?.length
                          ? form.values.files.map((file, index) => (
                              <Box className='flex flex-row gap-[10px]'>
                                <Button
                                  className='block font-normal p-0 h-auto truncate'
                                  key={index}
                                  onClick={() => downloadFile(file)}
                                >
                                  • {getFileName(file)}
                                </Button>
                                <IconButton
                                  onClick={() => {
                                    form.setFieldValue(
                                      'files',
                                      form.values.files.filter((_, fileIndex) => fileIndex !== index)
                                    );
                                  }}
                                >
                                  <DeleteBin6LineIcon fill='#ED1002' />
                                </IconButton>
                              </Box>
                            ))
                          : ''}
                      </Box>
                    </Box>
                    <Box>
                      <Box className='mb-[7px]'>
                        <Box {...getRootProps()}>
                          <Box component='input' {...getInputProps()} />
                          <Box className='flex w-[216px] items-center justify-center rounded-[8px] p-[7px] border border-[#C6C6C6] cursor-pointer'>
                            <UploadIcon className='w-5 h-5 mr-auto' />
                            <Box className='mr-auto'>{t('selectFile', { ns: 'admin/common' })}</Box>
                          </Box>
                        </Box>
                      </Box>
                      <Box className='text-[11px] w-[216px] text-center'>
                        {t('dragOrDropFile', { ns: 'admin/common' })}
                      </Box>
                    </Box>
                  </BaseRow>
                </>
              ) : null}

              {props.mode === DIALOG_MODE_VIEW ? (
                <>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.name')}:</Box>
                    <Box>{distributor?.name}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.username')}:</Box>
                    <Box>{distributor?.username}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px] items-center'>
                    <Box className='font-bold'>{t('editDialog.level')}:</Box>
                    <Box className='w-[131px]'>{getLevel(Number(distributor?.distributor_level))}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.address')}:</Box>
                    <Box>{distributor?.address}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.representative')}:</Box>
                    <Box>{distributor?.representative}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.phone')}:</Box>
                    <Box>{distributor?.phone}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.email')}:</Box>
                    <Box>{distributor?.email}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.contract_id')}:</Box>
                    <Box>{distributor?.contract_id}</Box>
                  </Box>
                  <Box className='flex flex-row gap-[15px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.created_at')}:</Box>
                    <Box>{distributor?.created_at ? dayjs(distributor.created_at).format('DD/MM/YYYY') : ''}</Box>
                  </Box>
                  <Box className='flex flex-col gap-[10px] mb-[16px]'>
                    <Box className='font-bold'>{t('editDialog.files')}:</Box>
                    <Box>
                      {distributor?.files?.length
                        ? distributor.files.map((file) => (
                            <Button className='block font-normal p-0 h-auto' onClick={() => downloadFile(file)}>
                              • {getFileName(file)}
                            </Button>
                          ))
                        : ''}
                    </Box>
                  </Box>
                </>
              ) : null}
              {props.mode === DIALOG_MODE_VIEW || props.mode === DIALOG_MODE_EDIT ? (
                <>
                  <Divider
                    className='order-last'
                    sx={{
                      borderColor: '#DFDFDF',
                      borderBottomWidth: 1,
                      width: '100%',
                      mt: 3,
                      mb: 2,
                      height: '100%',
                    }}
                  ></Divider>
                  <Box className='mb-4 order-last'>
                    <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
                      <Box className='font-bold text-[#005F73] text-[18px]'>{t('editDialog.agentList')}</Box>
                      <Box className='flex gap-[6.5px]'>
                        <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
                          {agentList?.length}
                        </Box>
                        {props.mode === DIALOG_MODE_EDIT && (
                          <AddNewIcon3
                            className='cursor-pointer ml-auto'
                            onClick={() => {
                              modeAgent.current = DIALOG_MODE_CREATE;
                              setIsOpenEditAgentDialog(true);
                            }}
                          />
                        )}
                      </Box>
                    </BaseRow>
                    {agentList?.length ? (
                      <BaseScrollbar maxHeight='250px'>
                        <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                          <Table>
                            <TableBody className='divide-y divide-[#005F73]'>
                              {agentList.map((row, index) => {
                                return (
                                  <TableRow key={index}>
                                    <TableCell className='border-none'>
                                      <Box className='font-bold'>{row.name}</Box>
                                      <Box>{row.username}</Box>
                                    </TableCell>
                                    <TableCell className='border-none text-right'>
                                      <Box component='span' className='font-bold text-[#ED1002]'>
                                        {row.total_devices}
                                      </Box>{' '}
                                      {t('editDialog.device')}
                                    </TableCell>
                                    <TableCell className='border-none w-[80px]'>
                                      <Box className='flex'>
                                        <IconButton
                                          onClick={() => {
                                            dataDetail.current = row;
                                            roleType.current = ROLE_AGENT;
                                            modeAgent.current = DIALOG_MODE_VIEW;
                                            setIsOpenEditAgentDialog(true);
                                          }}
                                        >
                                          <InformationFillIcon fill='#D9D9D9' />
                                        </IconButton>
                                        {props.mode === DIALOG_MODE_EDIT && (
                                          <IconButton
                                            onClick={() => {
                                              dataDetail.current = row;
                                              roleType.current = ROLE_AGENT;
                                              setIsOpenDeleteConfirm(true);
                                            }}
                                          >
                                            <DeleteBin6LineIcon fill='#ED1002' className='!w-[18px]' />
                                          </IconButton>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </BaseScrollbar>
                    ) : null}
                  </Box>
                </>
              ) : null}
            </Box>

            {props.mode === DIALOG_MODE_VIEW || props.mode === DIALOG_MODE_EDIT ? (
              <Box className='w-1/2 ml-6'>
                <Box className='flex flex-col'>
                  <Box className={props?.mode === DIALOG_MODE_EDIT ? 'order-1' : 'order-3 mt-3'}>
                    <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
                      <Box className='font-bold text-[#005F73] text-[18px]'>{t('editDialog.facilityList')}</Box>
                      <Box className='flex gap-[6.5px]'>
                        <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
                          {facilityList?.length}
                        </Box>
                      </Box>
                    </BaseRow>
                    <BaseRow className='mb-3 gap-[8px] text-[13px]'>
                      <Button
                        variant={facilityFilter === -1 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#005F73] hover:border-[#005F73] hover:text-white hover:bg-[#005F73] ${
                          facilityFilter === -1 ? 'text-white bg-[#005F73]' : ' text-[#005F73] border-[#005F73]'
                        }`}
                        onClick={() => setFacilityFilter(-1)}
                      >
                        {t('statusOptions.all')}
                      </Button>
                      <Button
                        variant={facilityFilter === 0 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#72777B] hover:border-[#72777B] hover:text-white hover:bg-[#72777B] ${
                          facilityFilter === 0 ? 'text-white bg-[#72777B]' : ' text-[#72777B] border-[#72777B]'
                        }`}
                        onClick={() => setFacilityFilter(0)}
                      >
                        {t('statusOptions.0')} ({facilityList?.filter((item) => item.deployment_status === 0)?.length})
                      </Button>
                      <Button
                        variant={facilityFilter === 1 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#FF9500] hover:border-[#FF9500] hover:text-white hover:bg-[#FF9500] ${
                          facilityFilter === 1 ? 'text-white bg-[#FF9500]' : ' text-[#FF9500] border-[#FF9500]'
                        }`}
                        onClick={() => setFacilityFilter(1)}
                      >
                        {t('statusOptions.1')} ({facilityList?.filter((item) => item.deployment_status === 1)?.length})
                      </Button>
                      <Button
                        variant={facilityFilter === 2 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#3D9149] hover:border-[#3D9149] hover:text-white hover:bg-[#3D9149] ${
                          facilityFilter === 2 ? 'text-white bg-[#3D9149]' : ' text-[#3D9149] border-[#3D9149]'
                        }`}
                        onClick={() => setFacilityFilter(2)}
                      >
                        {t('statusOptions.2')} ({facilityList?.filter((item) => item.deployment_status === 2)?.length})
                      </Button>
                    </BaseRow>
                    <BaseScrollbar maxHeight='300px'>
                      {facilityList?.length ? (
                        <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                          <Table>
                            <TableBody className='divide-y divide-[#005F73]'>
                              {facilityList
                                .filter((item) => {
                                  if (facilityFilter === -1) return true;
                                  return item.deployment_status === facilityFilter;
                                })
                                .map((row, index) => {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell className='border-none'>
                                        <Box className='font-bold'>{row.location_name}</Box>
                                        <Box>{row.full_address}</Box>
                                      </TableCell>
                                      <TableCell className='border-none text-right'>
                                        <Box component='span' className='font-bold text-[#ED1002]'>
                                          {row.device_count}
                                        </Box>{' '}
                                        {t('editDialog.device')}
                                        <Box className='font-bold'>
                                          {row.deployment_status === 2 ? (
                                            <Box className='text-[#3D9149]'>{t('facilityStatus.activated')}</Box>
                                          ) : row.deployment_status === 1 ? (
                                            <Box className='text-[#FF9500]'>{t('facilityStatus.deployed')}</Box>
                                          ) : (
                                            <Box className='text-[#72777B]'>{t('facilityStatus.pending')}</Box>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell className='border-none w-[80px]'>
                                        <IconButton
                                          onClick={() => {
                                            setFacility(row);
                                            setIsOpenDetailFacilityDialog(true);
                                          }}
                                        >
                                          <InformationFillIcon fill='#D9D9D9' />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : null}
                    </BaseScrollbar>
                  </Box>
                  <Box className={props?.mode === DIALOG_MODE_EDIT ? 'order-2 my-3' : 'order-1'}>
                    <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
                      <Box className='font-bold text-[#005F73] text-[18px]'>{t('editDialog.userList')}</Box>
                      <Box className='flex gap-[6.5px]'>
                        <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
                          {userList?.length}
                        </Box>
                      </Box>
                    </BaseRow>
                    {userList?.length ? (
                      <BaseScrollbar maxHeight='300px'>
                        <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                          <Table>
                            <TableBody className='divide-y divide-[#005F73]'>
                              {userList.map((row, index) => {
                                return (
                                  <TableRow key={index}>
                                    <TableCell className='border-none'>
                                      <Box className='font-bold'>{row.name}</Box>
                                      <Box className='mb-[5px]'>{row.username}</Box>
                                    </TableCell>
                                    <TableCell className='border-none text-right'>
                                      <Box component='span' className='font-bold text-[#ED1002]'>
                                        {row.total_devices}
                                      </Box>{' '}
                                      {t('editDialog.device')}
                                    </TableCell>
                                    <TableCell className='border-none w-[80px]'>
                                      <Box className='flex'>
                                        <IconButton
                                          onClick={() => {
                                            dataDetail.current = row;
                                            roleType.current = ROLE_USER;
                                            setIsOpenDetailUserDialog(true);
                                          }}
                                        >
                                          <InformationFillIcon fill='#D9D9D9' />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </BaseScrollbar>
                    ) : null}
                  </Box>
                  <Box className={props?.mode === DIALOG_MODE_EDIT ? 'order-3' : 'order-2'}>
                    <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
                      <Box className='font-bold text-[#005F73] text-[18px]'>{t('editDialog.equipmentList')}</Box>
                      <Box className='flex gap-[10px]'>
                        <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                          {equipmentList?.length}
                        </Box>
                      </Box>
                    </BaseRow>
                    <BaseRow className='mb-3 gap-[8px] text-[13px]'>
                      <Button
                        variant={equipmentFilter === -1 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#005F73] hover:border-[#005F73] hover:text-white hover:bg-[#005F73] ${
                          equipmentFilter === -1 ? 'text-white bg-[#005F73]' : ' text-[#005F73] border-[#005F73]'
                        }`}
                        onClick={() => setEquipmentFilter(-1)}
                      >
                        {t('deviceStatus.all')}
                      </Button>
                      <Button
                        variant={equipmentFilter === 1 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#ED1002] hover:border-[#ED1002] hover:text-white hover:bg-[#ED1002]  ${
                          equipmentFilter === 1 ? 'text-white bg-[#ED1002]' : ' text-[#ED1002] border-[#ED1002]'
                        }`}
                        onClick={() => setEquipmentFilter(1)}
                      >
                        {t('deviceStatus.1')} ({equipmentList?.filter((item) => item.device_status === 1)?.length})
                      </Button>
                      <Button
                        variant={equipmentFilter === 0 ? 'contained' : 'outlined'}
                        className={`rounded-[8px] font-bold py-[9px] px-[20px] border border-[#3D9149] hover:border-[#3D9149] hover:text-white hover:bg-[#3D9149] ${
                          equipmentFilter === 0 ? 'text-white bg-[#3D9149]' : 'border text-[#3D9149] border-[#3D9149] '
                        }`}
                        onClick={() => setEquipmentFilter(0)}
                      >
                        {t('deviceStatus.0')} ({equipmentList?.filter((item) => item.device_status === 0)?.length})
                      </Button>
                    </BaseRow>
                    {equipmentList?.length ? (
                      <BaseScrollbar maxHeight='300px'>
                        <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                          <Table>
                            <TableBody className='divide-y divide-[#005F73]'>
                              {equipmentList
                                .filter((item) => {
                                  if (equipmentFilter === -1) return true;
                                  return item.device_status === equipmentFilter;
                                })
                                .map((row, index) => {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell className='border-none'>
                                        <Box className='font-bold'>{row.device_code}</Box>
                                        <Box>{row.location_name}</Box>
                                      </TableCell>
                                      <TableCell className='border-none text-right'>
                                        <Box className='font-bold'>{row.device_name}</Box>
                                        <Box className='flex flex-col gap-[4px] justify-end'>
                                          {getStatus(row.sim_status)} {getStatus(row.iot_status)}
                                        </Box>
                                      </TableCell>
                                      <TableCell className='border-none w-[80px]'>
                                        <Box className='flex'>
                                          <IconButton
                                            onClick={() => {
                                              (async () => {
                                                const equipment = await getEquipmentDetail(row);
                                                dataDetail.current = equipment;
                                                roleType.current = 'equipment';
                                                setIsOpenDetailEquipmentDialog(true);
                                              })();
                                            }}
                                          >
                                            <InformationFillIcon fill='#D9D9D9' />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </Box>
                      </BaseScrollbar>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            ) : null}
          </BaseRow>
        </DialogContent>

        {props.mode === DIALOG_MODE_CREATE || props.mode === DIALOG_MODE_EDIT ? (
          <DialogActions>
            {props.mode === DIALOG_MODE_EDIT && (
              <Button
                className='bg-[#ED1002] text-white w-[125px] h-[35px] hover:bg-[#005F73]'
                onClick={() => {
                  isSendEmail.current = false;
                  form.handleSubmit();
                }}
              >
                {t('save', { ns: 'admin/common' })}
              </Button>
            )}
            {props.mode === DIALOG_MODE_CREATE ? (
              <Button
                className='bg-[#ED1002] text-white w-[130px] h-[35px] hover:bg-[#005F73]'
                onClick={() => {
                  if (!form.values.email) {
                    return handleApiError(t('editDialog.emailRequired'));
                  }
                  isSendEmail.current = true;
                  form.handleSubmit();
                }}
              >
                {t('saveAndSendEmail', { ns: 'admin/common' })}
              </Button>
            ) : null}
          </DialogActions>
        ) : null}
      </Dialog>

      {/* agent */}
      {isOpenEditAgentDialog ? (
        <DetailAgentDialog
          open={isOpenEditAgentDialog}
          setOpen={(open) => {
            setIsOpenEditAgentDialog(open);
            handleGetAgents();
          }}
          data={dataDetail.current}
          mode={modeAgent.current}
          parent_username={form.values.username}
        />
      ) : null}

      {/* user */}
      {isOpenDetailUserDialog ? (
        <ShowUserDialog open={isOpenDetailUserDialog} setOpen={setIsOpenDetailUserDialog} user={dataDetail.current} />
      ) : null}

      {/* equipment */}
      {isOpenDetailEquipmentDialog ? (
        <DetailEquipmentDialog
          open={isOpenDetailEquipmentDialog}
          setOpen={setIsOpenDetailEquipmentDialog}
          data={dataDetail.current}
        />
      ) : null}

      {/* facility */}
      {isOpenDetailFacilityDialog ? (
        <DetailFacilityDialog
          open={isOpenDetailFacilityDialog}
          data={facility}
          setOpen={setIsOpenDetailFacilityDialog}
        />
      ) : null}

      <SendPasswordDialog open={openSendPasswordDialog} setOpen={() => handleSetOpen(false, true)} data={distributor} />

      <ConfirmDialog
        open={isOpenDeleteConfirm}
        title={t(`deleteDialog.${roleType.current}.title`)}
        description={t(`deleteDialog.${roleType.current}.description`, {
          username: roleType.current === 'equipment' ? dataDetail.current?.device_code : dataDetail.current?.username,
        })}
        onConfirm={() => handleDelete()}
        onCancel={() => setIsOpenDeleteConfirm(false)}
      />
    </>
  );
};

export default memo(EditDistributorDialog);
